import moment from 'moment';
import Helpers from 'helpers/Helpers'; // chỉnh lại path nếu cần
import AdminLogApi from 'apis/api/AdminLog';

/* ============================================================================
 * TYPES & HELPERS CHUNG
 * ========================================================================== */

export type Row = Record<string, any>;

export interface Group {
  summaries: Row[];
  details: Row[];
  baseItem: Row;
}

export interface UpgradeRows {
  details: Row[];
  products: Row[];
}

export interface UpgradeProcessors {
  processProduct: (rows: Row[], isPreserveCost?: boolean) => Row[];
  processDetail: (rows: Row[]) => Row[];
}

export const isBlank = (v?: string | null) => !v || v === '';

export const EMPTY_HAN_DANG_KIEM = '0001-01-01T00:00:00';

/**
 * Hạn đăng kiểm trống/mặc định -> '0001-01-01T00:00:00'. Ngày hợp lệ giữ nguyên.
 * moment(new Date('0001-01-01...')) khi JSON hoá ra "0000-12-31T16:53:30.000Z" (giờ địa phương cũ +07:06:30),
 * server không nhận nên phải đổi lại trước khi gửi.
 */
export const fixEmptyHanDangKiem = (value: any) => {
  if (!value) return EMPTY_HAN_DANG_KIEM;
  const m = moment(value);
  return !m.isValid() || m.year() <= 2010 ? EMPTY_HAN_DANG_KIEM : value;
};

/** Server từ chối vì KT đổi/thêm sản phẩm mà bộ phận tạo đơn chưa xác nhận -> cần gửi YeuCauChinhSua */
/**
 * Config OM (Quản trị > OMConfig) — MẶC ĐỊNH BẬT (chưa tạo key / đọc lỗi vẫn tính là bật):
 * Bật: KT thêm/đổi SP chưa được xác nhận -> vẫn gửi FinishUpgradeV2 (lưu serial/SĐT) rồi tự gửi YeuCauChinhSua.
 * Tắt (data = '0' hoặc 'false'): chặn, chỉ gửi YeuCauChinhSua (dữ liệu KT vừa nhập chưa được lưu).
 */
export const CONFIG_UPGRADE_FINISH_NO_SUMMARY = 'UpgradeFinishNoSummary';

export const isUpgradeFinishNoSummaryOn = async (): Promise<boolean> => {
  const res = await AdminLogApi.GetConfigWithKey(CONFIG_UPGRADE_FINISH_NO_SUMMARY);
  if (res?.data?.error) return true; // chưa có key / lỗi -> giữ mặc định bật
  return !['0', 'false'].includes(
    String(res?.data?.data?.data ?? '')
      .trim()
      .toLowerCase()
  );
};

// chuẩn hoá NFC: server có thể trả tiếng Việt dạng dấu tổ hợp (NFD) làm regex không khớp
const normalizeMsg = (msg?: string) => (msg || '').normalize('NFC').replace(/\s+/g, ' ');

// Server có 2 dạng: "Sản phẩm A, B chưa được bộ phận tạo đơn xác nhận, vui lòng yêu cầu cập nhật"
// và ngắn "Vui lòng yêu cầu cập nhật" (không kèm tên SP)
export const isNeedRequestUpdateMsg = (msg?: string) =>
  /chưa được bộ phận tạo đơn xác nhận|vui lòng yêu cầu cập nhật/i.test(normalizeMsg(msg));

/**
 * ĐIỀU KIỆN CHUNG "SP cần người tạo đơn xác nhận" — dùng cho cả Thực hiện xong lẫn Hoàn tất đơn:
 * - detail không gắn summary nào (KT thêm SP) hoặc summary không còn
 * - summary đang requiresApproval (backend sinh khi KT đổi/thêm, KD chưa xác nhận)
 * - mã SP khác summary và không phải thành phần của bộ đó (KT đổi SP)
 */
export const needsCreatorConfirm = (detail: Row, summary: Row | undefined, productSets?: Row[]): boolean => {
  if (!summary || summary.requiresApproval) return true;
  const code = detail.maSanPhamMoi || detail.maSanPham || '';
  if (!code || code === summary.maSanPhamMoi) return false;
  return !getSetInfo(productSets, summary.maSanPhamMoi)?.partCodes.includes(code);
};

/** Tên các SP cần người tạo đơn xác nhận, tính trên dữ liệu đơn đã lưu (upgradeDetails/upgradeSummaries) */
export const findProductsNeedConfirm = (order?: Row | null, productSets?: Row[]): string[] => {
  const summaries: Row[] = order?.upgradeSummaries || [];
  const details: Row[] = order?.upgradeDetails || [];
  const byId = new Map(summaries.map(s => [s.id, s]));

  const fromDetails = details
    .filter(d => needsCreatorConfirm(d, byId.get(d.summaryId), productSets))
    .map(d => d.maSanPhamMoi || d.maSanPham || '');
  // summary chờ duyệt mà chưa có detail nào trỏ tới
  const linked = new Set(details.map(d => d.summaryId));
  const fromSummaries = summaries.filter(s => s.requiresApproval && !linked.has(s.id)).map(s => s.maSanPhamMoi);

  return [...fromDetails, ...fromSummaries].filter(Boolean);
};

/** Nội dung cho modal yêu cầu cập nhật: tên SP trong message -> SP cần xác nhận của đơn -> nguyên message */
export const resolveRequestUpdateSource = (msg: string, order?: Row | null, productSets?: Row[]): string | string[] => {
  if (parseUnconfirmedProducts(msg).length > 0) return msg;
  const pending = findProductsNeedConfirm(order, productSets);
  return pending.length > 0 ? pending : msg;
};

/** "Sản phẩm A, B chưa được bộ phận tạo đơn xác nhận..." -> ['A', 'B']. Không đúng mẫu thì trả [] */
export const parseUnconfirmedProducts = (msg?: string): string[] => {
  const m = normalizeMsg(msg).match(/Sản phẩm\s+(.+?)\s+chưa được bộ phận tạo đơn xác nhận/i);
  return m
    ? m[1]
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];
};

/**
 * Mã sản phẩm MỚI hiện tại của một dòng.
 * Lưới Details sửa `maSanPham`, lưới Products sửa `maSanPhamMoi`,
 * nên so với `preMaSanPham` (mã đã chuẩn hóa ở lần xử lý trước) để biết field nào vừa đổi.
 */
export const resolveNewMaSP = (item?: Row): string => {
  const a = item?.maSanPham || '';
  const b = item?.maSanPhamMoi || '';
  const pre = item?.preMaSanPham || '';
  if (pre) {
    if (a && a !== pre) return a;
    if (b && b !== pre) return b;
    return pre;
  }
  return b || a; // lần đầu load: ưu tiên mã mới (BM)
};

const plateKey = (item?: Row) => `${item?.registrationPlate || 'NO_PLATE'}_${item?.companyId || 0}`;

const findSet = (productSets: Row[] | undefined, code?: string) => productSets?.find(ps => ps.maSanPham === code);

const hasSetDetails = (set?: Row) => !!set?.details?.length;

/** Bộ sản phẩm (có thành phần) của 1 mã SP, kèm danh sách mã thành phần. Không phải bộ -> null */
export const getSetInfo = (productSets: Row[] | undefined, code?: string) => {
  const set = findSet(productSets, code);
  if (!hasSetDetails(set)) return null;
  const partCodes: string[] = set!.details
    .map(({ productInfo, ...rest }: Row) => productInfo?.maSanPham || rest.maSanPham)
    .filter(Boolean);
  return { set: set!, partCodes };
};

const pick = <T>(preferred: T | undefined, fallback: T) => (preferred !== undefined ? preferred : fallback);

const joinField = (rows: Row[], field: string, sep: string, fallback?: string) =>
  rows
    .map(r => r[field])
    .filter(Boolean)
    .join(sep) ||
  fallback ||
  '';

/* ============================================================================
 * 1. XỬ LÝ GIÁ / MÃ SP CHO TỪNG DÒNG (thay proccessProductUG / proccessDetailUG)
 * ========================================================================== */

interface ProcessorCtx {
  listProMix: Row[];
  canViewPrice: boolean;
  rowSelected?: Row;
  /** true: đổi sản phẩm thì lấy giá gốc của sản phẩm mới. Mặc định false (giữ hành vi cũ: giữ giá đã có). */
  repriceOnChange?: boolean;
}

export function createUpgradeProcessors(ctx: ProcessorCtx): UpgradeProcessors {
  const { listProMix, canViewPrice, rowSelected, repriceOnChange = false } = ctx;
  const findPM = (code?: string) => listProMix.find(p => p.maSanPham == code);

  const resolvePrice = (item: Row, pm: Row | undefined, changed: boolean) => {
    if (!canViewPrice) return { cost: 0, vatValue: 0, priceAfterVat: 0 };

    const useCatalog = repriceOnChange && changed;
    const cost = useCatalog ? pm?.giaBanSi : item?.cost != null ? item.cost : pm?.giaBanSi;
    const vat = useCatalog ? pm?.vatValue : item?.vatValue ? item.vatValue : pm?.vatValue;

    return {
      cost: +cost || 0,
      vatValue: vat || 0,
      priceAfterVat: +cost + (+cost * +vat) / 100 || 0
    };
  };

  const processProduct = (rows: Row[], _isPreserveCost = true): Row[] =>
    (rows || []).map(dtl => {
      const code = resolveNewMaSP(dtl);
      const pm = findPM(code);
      const changed = !!dtl?.preMaSanPham && code !== dtl.preMaSanPham;
      const note = rowSelected?.upgradeDetails?.find((ud: Row) => ud.id == dtl.id)?.note || '';

      return {
        ...dtl,
        ...resolvePrice(dtl, pm, changed),
        note,
        maSanPham: code,
        maSanPhamMoi: code,
        preMaSanPham: code,
        isRequireSP: pm?.input || 0,
        isRequireSPOld: findPM(dtl?.maSanPhamCu)?.input || 0
      };
    });

  const detailKey = (item: Row) =>
    [resolveNewMaSP(item) + item.cost + item.old, item.maBoSanPham || item.setName || '', item.staffExecute || ''].join(
      '_'
    );

  const processDetail = (rows: Row[]): Row[] => {
    // gom nhóm theo key
    const groups = new Map<string, Row[]>();
    (rows || []).forEach(item => {
      const key = detailKey(item);
      groups.set(key, [...(groups.get(key) || []), item]);
    });

    const out: Row[] = [];

    groups.forEach(children => {
      const first = children[0];
      const code = resolveNewMaSP(first);
      const pm = findPM(code);

      const common = (item: Row, itemCode: string) => {
        const changed = !!item?.preMaSanPham && itemCode !== item.preMaSanPham;
        return {
          ...item,
          ...resolvePrice(item, pm, changed),
          maSanPham: itemCode,
          maSanPhamMoi: itemCode,
          preMaSanPham: itemCode,
          note: '',
          isRequireSP: pm?.input || 0,
          maBoSanPham: item?.maBoSanPham || '',
          setName: item?.setName || item?.maBoSanPham || '',
          staffExecute: item?.staffExecute || ''
        };
      };

      if (pm?.input === 0) {
        // Không cần serial -> gộp số lượng
        const ids = [...new Set([...children.map(c => c.id || 0), ...(first.ids || [])])];
        out.push({
          ...common(first, code),
          ids,
          amount: (first.amount || 0) + (children.length - ((first.amount && 1) || 0))
        });
      } else {
        // Cần serial -> tách từng dòng
        children.forEach(dtl => out.push(common(dtl, resolveNewMaSP(dtl))));
      }
    });

    return out;
  };

  return { processProduct, processDetail };
}

/* ============================================================================
 * 2. LOAD DỮ LIỆU -> UI (thay processUpgradeItems)
 * ========================================================================== */

/** Gom summaries + details theo biển số + công ty, đồng thời merge staffExecute vào summary */
export function groupUpgrades(summaries: Row[] = [], details: Row[] = []): Map<string, Group> {
  const staffByKey = new Map<string, Set<string>>();
  details.forEach(d => {
    if (!d.staffExecute) return;
    const key = plateKey(d);
    const set = staffByKey.get(key) ?? new Set<string>();
    set.add(d.staffExecute);
    staffByKey.set(key, set);
  });

  const groups = new Map<string, Group>();
  const add = (item: Row, type: 'summaries' | 'details') => {
    const key = plateKey(item);
    if (!groups.has(key)) groups.set(key, { summaries: [], details: [], baseItem: item });
    groups.get(key)![type].push(item);
  };

  summaries.forEach(s => {
    const staffSet = staffByKey.get(plateKey(s));
    add({ ...s, staffExecute: s.staffExecute || (staffSet ? [...staffSet].join(', ') : '') }, 'summaries');
  });
  details.forEach(d => add(d, 'details'));

  return groups;
}

/** Rã bộ cho đơn kỹ thuật chưa có details: ruột phụ giá 0, thiết bị chính tách riêng */
function unbundleForNewOrder(set: Row, summary: Row): { main?: Row; parts: Row[] } {
  const parts = set.details.map(({ productInfo, ...rest }: Row) => ({
    ...rest,
    ...productInfo,
    id: 0,
    summaryId: summary.id,
    maBoSanPham: set.description,
    setName: set.description,
    cost: 0,
    priceAfterVat: 0,
    vatValue: 0
  }));

  const mainIdx = parts.findIndex((d: Row) => d.input === 1);
  if (mainIdx === -1) return { parts };

  const [mainPart] = parts.splice(mainIdx, 1);
  return {
    main: { ...summary, summaryId: summary.id, maSanPhamMoi: mainPart.maSanPham, setName: set.description },
    parts
  };
}

/** Rã bộ cho summary chưa có details (đã có details khác): giữ thiết bị chính trong danh sách */
function unbundleForExisting(set: Row, summary: Row): Row[] {
  return set.details.map(({ productInfo, ...rest }: Row) => ({
    ...rest,
    ...productInfo,
    summaryId: summary.id,
    id: 0,
    maSanPhamCu: rest.input === 1 ? summary.maSanPhamCu : '',
    maSanPhamMoi: rest.maSanPham,
    maBoSanPham: set.description,
    setName: set.description
  }));
}

function buildTechnicianRows(
  group: Group,
  validSummaries: Row[],
  productSets: Row[],
  P: UpgradeProcessors
): UpgradeRows {
  const out: UpgradeRows = { details: [], products: [] };
  const pushProducts = (rows: Row[]) => out.products.push(...P.processProduct(rows, true));
  const pushDetails = (rows: Row[]) => out.details.push(...P.processDetail(rows));

  // --- Chưa có details: seed từ summaries ---
  if (group.details.length === 0) {
    validSummaries.forEach(summary => {
      if (isBlank(summary.maSanPhamCu)) {
        // Sản phẩm lẻ mới -> đẩy xuống Details để nhập serial
        pushDetails([{ ...summary, summaryId: summary.id, id: 0 }]);
        return;
      }

      const set = findSet(productSets, summary.maSanPhamMoi);
      if (hasSetDetails(set)) {
        const { main, parts } = unbundleForNewOrder(set!, summary);
        pushProducts([main ?? { ...summary, summaryId: summary.id }]);
        pushDetails(parts);
      } else {
        pushProducts([{ ...summary, summaryId: summary.id }]); // bộ lỗi (không có ruột)
      }
    });
    return out;
  }

  // --- Đã có details: bổ sung cho summary chưa có detail ---
  const details = [...group.details];
  validSummaries.forEach(summary => {
    const hasExisting = details.some(d => d.summaryId === summary.id || d.id === summary.id);
    if (hasExisting) return;

    const set = isBlank(summary.maSanPhamCu) ? undefined : findSet(productSets, summary.maSanPhamMoi);
    if (hasSetDetails(set)) details.push(...unbundleForExisting(set!, summary));
    else details.push({ ...summary, summaryId: summary.id, id: 0 });
  });

  // --- Phân loại vỏ / ruột ---
  details.forEach(detail => {
    const summary = validSummaries.find(s => s.id === detail.summaryId || s.id === detail.id);
    const setName = findSet(productSets, summary?.maSanPhamMoi)?.description;
    const isPartOfSet = !!setName;

    const costs = {
      cost: pick(summary?.cost, detail.cost),
      priceAfterVat: pick(summary?.priceAfterVat, detail.priceAfterVat),
      vatValue: pick(summary?.vatValue, detail.vatValue)
    };
    if (!isPartOfSet) costs.priceAfterVat = costs.cost;

    if (isBlank(detail.maSanPhamCu)) {
      // Hàng lẻ hoặc ruột phụ -> lưới Details
      if (isPartOfSet) Object.assign(costs, { cost: 0, priceAfterVat: 0, vatValue: 0 });
      pushDetails([{ ...detail, ...costs, setName }]);
    } else {
      // Vỏ (thiết bị chính đổi trả) -> lưới Products
      const code = resolveNewMaSP(detail);
      pushProducts([
        {
          ...detail,
          ...costs,
          summaryId: summary ? summary.id : detail.summaryId,
          maSanPhamMoi: code,
          maSanPham: code,
          setName
        }
      ]);
    }
  });

  return out;
}

/** Các thay đổi mà kỹ thuật đã làm so với summary (dùng cho modal xác nhận của kinh doanh) */
function getTechChanges(summary: Row, existingDetails: Row[], productSets: Row[]): Row[] {
  if (hasSetDetails(findSet(productSets, summary.maSanPhamMoi))) return []; // hàng bộ -> bỏ qua

  return existingDetails
    .filter(d => d.id && d.id !== 0)
    .flatMap(d => {
      const changes: Row[] = [];
      const spMoi = d.maSanPhamMoi || d.maSanPham;
      if (spMoi && summary.maSanPhamMoi && spMoi !== summary.maSanPhamMoi) {
        changes.push({
          loai: 'DOI_SP_MOI',
          idSummary: summary.id,
          idDetail: d.id,
          truoc: summary.maSanPhamMoi,
          sau: spMoi,
          serial: d.serial,
          serialCu: d.serialCu,
          bs: d.registrationPlate,
          companyId: d.companyId ?? summary.companyId ?? 0,
          staff: d.staffExecute,
          chenhLech: (d.cost ?? 0) - (summary.cost ?? 0),
          detail: d
        });
      }

      const cuS = summary.maSanPhamCu || '';
      const cuD = d.maSanPhamCu || '';
      if (cuS && cuD && cuS !== cuD) {
        changes.push({
          loai: 'DOI_SP_CU',
          idSummary: summary.id,
          idDetail: d.id,
          truoc: cuS,
          sau: cuD,
          serialCu: d.serialCu,
          bs: d.registrationPlate,
          detail: d
        });
      }
      return changes;
    });
}

function buildSalesRows(group: Group, validSummaries: Row[], productSets: Row[], P: UpgradeProcessors): UpgradeRows {
  let products: Row[] = [];

  if (validSummaries.length === 0 && group.details.length > 0) {
    // Chỉ có details -> dồn vào products, bước dưới sẽ tách vỏ/ruột
    group.details.forEach(detail => {
      products.push(...P.processProduct([{ ...detail, summaryId: detail.summaryId || detail.id }], true));
    });
  } else {
    validSummaries.forEach(summary => {
      const existing = group.details.filter(d => d.summaryId === summary.id || d.id === summary.id);
      const techChanges = getTechChanges(summary, existing, productSets);

      const row: Row = {
        ...summary,
        summaryId: summary.id,
        isChangedByTech: techChanges.length > 0,
        techChanges
      };

      // Kỹ thuật đã nhập serial/phone/note -> gom hiển thị cho kinh doanh
      if (existing.some(d => d.serial || d.phone || d.note || d.serialCu || d.phoneCu)) {
        row.serial = joinField(existing, 'serial', ', ', summary.serial);
        row.phone = joinField(existing, 'phone', ', ', summary.phone);
        row.note = joinField(existing, 'note', '; ', summary.note);
        row.serialCu = joinField(existing, 'serialCu', ', ', summary.serialCu);
        row.phoneCu = joinField(existing, 'phoneCu', ', ', summary.phoneCu);
      }

      products.push(...P.processProduct([row], true));
    });

    // SP kỹ thuật thêm (detail không gắn summary nào) -> hiện thành dòng mới chờ KD duyệt.
    // id = 0 để khi lưu thành summary mới; orphanDetailId giữ lại detail gốc của KT.
    const summaryIds = new Set(validSummaries.map(s => s.id));
    group.details
      .filter(d => !(d.summaryId && summaryIds.has(d.summaryId)) && !summaryIds.has(d.id))
      .forEach(orphan => {
        const maSP = orphan.maSanPhamMoi || orphan.maSanPham || '';
        const [row] = P.processProduct(
          [
            {
              ...orphan,
              id: 0,
              summaryId: 0,
              orphanDetailId: orphan.id,
              isChangedByTech: true,
              techChanges: [
                {
                  loai: 'THEM_SP_MOI',
                  idSummary: 0,
                  idDetail: orphan.id,
                  truoc: '',
                  sau: maSP,
                  serial: orphan.serial,
                  bs: orphan.registrationPlate,
                  companyId: orphan.companyId ?? 0,
                  staff: orphan.staffExecute,
                  detail: orphan
                }
              ]
            }
          ],
          true
        );
        products.push({ ...row, note: orphan.note || row.note });
      });
  }

  // Dòng không có maSanPhamCu -> chuyển xuống lưới Details
  const details = products.filter(p => isBlank(p.maSanPhamCu));
  products = products.filter(p => !isBlank(p.maSanPhamCu));
  return { details, products };
}

const sortByRequireSP = (a: Row, b: Row) =>
  a.isRequireSP !== 0 && b.isRequireSP !== 0
    ? a.isRequireSP - b.isRequireSP
    : (b.isRequireSP || 0) - (a.isRequireSP || 0);

const stripEmptyIds = (rows: Row[]) =>
  rows.forEach(r => {
    if (Array.isArray(r.ids) && r.ids.length === 1 && r.ids[0] === 0) delete r.ids;
  });

export function buildUpgradeItems(args: {
  summaries?: Row[];
  details?: Row[];
  productSets?: Row[];
  isTechnician: boolean;
  processors: UpgradeProcessors;
}): Row[] {
  const { summaries = [], details = [], productSets = [], isTechnician, processors } = args;
  if (summaries.length === 0 && details.length === 0) return [];

  return [...groupUpgrades(summaries, details).values()].map(group => {
    // Lọc summary trùng với detail
    const validSummaries = group.summaries.filter(s => !(s.id !== 0 && group.details.some(d => d.id === s.id)));

    const rows = isTechnician
      ? buildTechnicianRows(group, validSummaries, productSets, processors)
      : buildSalesRows(group, validSummaries, productSets, processors);

    rows.details.sort(sortByRequireSP);
    stripEmptyIds(rows.details);
    stripEmptyIds(rows.products);

    const { baseItem } = group;
    return {
      ...baseItem,
      hanDangKiem: baseItem?.hanDangKiem ? moment(new Date(baseItem.hanDangKiem)) : null,
      maSanPhamMoi: rows.products[0]?.maSanPhamMoi || rows.details[0]?.maSanPhamMoi || '',
      maSanPhamCu: rows.products[0]?.maSanPhamCu || '',
      cost: 0,
      priceAfterVat: 0,
      vatValue: 0,
      details: rows.details,
      products: rows.products
    };
  });
}

/* ============================================================================
 * 3. SUBMIT: UI -> PAYLOAD (thay khối `if (keyOrder === 7)` trong handleFormSubmit)
 * ========================================================================== */

interface PayloadCtx {
  rowSelected: Row;
  useVAT: boolean;
}

const parseHanDangKiem = (value: any): string | null => {
  if (!value) return null;
  const valid = moment(value).isValid() || value?._isValid;
  if (!valid || Number((value + '').split('-')[0]) <= 2010) return null;
  return Helpers.ConvertDateToISOString(new Date(value), false);
};

/** Map thông tin xe từ group xuống item, nhân bản nếu amount > 1 */
const expandItem = (item: Row, group: Row): Row[] => {
  const mapped: any = {
    ...item,
    vehicleType: group.vehicleType || item.vehicleType || '',
    registrationPlate: group.registrationPlate?.trim() || item.registrationPlate?.trim() || '',
    companyId: group.companyId || item.companyId || 0,
    doiXe: group.doiXe || item.doiXe || '',
    hanDangKiem: group.hanDangKiem || item.hanDangKiem
  };

  const amount = mapped.amount || 1;
  if (amount <= 1) return [mapped];

  const ids = mapped.ids || [];
  return Array.from({ length: amount }, (_, k) => ({ ...mapped, id: ids[k] || 0 }));
};

/** Chuẩn hóa 1 dòng theo format server */
const formatItem = (item: Row, isSummary: boolean, { rowSelected, useVAT }: PayloadCtx): Row => ({
  id: item.id || 0,
  quantity: item.quantity || 1,
  note: rowSelected?.upgradeDetails?.find((ud: Row) => ud.id == item.id)?.note || item.note || '',
  phone: item.phone || '',
  serial: item.serial || '',
  phoneCu: item.phoneCu || '',
  serialCu: item.serialCu || '',
  maSanPhamMoi: resolveNewMaSP(item),
  maSanPhamCu: item.maSanPhamCu || '',
  vehicleType: item.vehicleType || '',
  registrationPlate: item.registrationPlate?.trim() || '',
  companyId: item.companyId || 0,
  doiXe: item.doiXe || '',
  priceAfterVat: useVAT ? item.priceAfterVat || 0 : item.cost || 0,
  vatValue: useVAT ? item.vatValue || 0 : 0,
  cost: item.cost || 0,
  hanDangKiem: parseHanDangKiem(item.hanDangKiem),
  summaryId: isSummary ? 0 : item.summaryId || item.idSumaries || 0
});

/**
 * Đồng bộ giá summary -> details (chỉ role KD/kế toán).
 * - Hàng bộ: chỉ thiết bị chính (có maSanPhamCu) nhận giá, ruột phụ = 0
 * - Hàng lẻ: detail nhận giá của summary
 */
export function syncCostSummaryToDetails(
  summaries: Row[],
  details: Row[],
  productSets: Row[] | undefined,
  useVAT: boolean
): Row[] {
  const byId = new Map(summaries.map(s => [s.id, s]));

  return details.map(d => {
    const sid = d.summaryId || d.idSumaries || 0;
    if (!sid) return d; // detail chưa gắn summary (SP KT thêm) -> không lấy nhầm summary mới id = 0
    const summary = byId.get(sid);
    if (!summary) return d;

    const isSet = hasSetDetails(findSet(productSets, summary.maSanPhamMoi));
    const isMain = !isBlank(d.maSanPhamCu);

    if (isSet && !isMain) return { ...d, cost: 0, vatValue: 0, priceAfterVat: 0 };

    return {
      ...d,
      cost: summary.cost ?? d.cost ?? 0,
      vatValue: useVAT ? summary.vatValue ?? 0 : 0,
      priceAfterVat: useVAT ? summary.priceAfterVat ?? 0 : summary.cost ?? 0
    };
  });
}

function buildTechnicianPayload(formItems: Row[], ctx: PayloadCtx) {
  const summaries = (ctx.rowSelected?.upgradeSummaries || []).map((s: Row) => formatItem(s, true, ctx));
  let isNotEnoughData = false;

  // formItems đã được expandItem (amount/ids) ở bước trước nên không bung lại lần nữa
  const details = formItems.map(item => {
    const missingSerial = item.isRequireSP == 1 && !item.serial?.trim();
    const missingPhone = item.isRequireSP == 2 && !item.phone?.trim();
    if (missingSerial || missingPhone) isNotEnoughData = true;
    return formatItem(item, false, ctx);
  });

  return { summaries, details, isNotEnoughData };
}

function buildSalesPayload(formItems: Row[], productSets: Row[] | undefined, ctx: PayloadCtx) {
  const { rowSelected } = ctx;
  const summaries = formItems.map(item => formatItem(item, true, ctx));
  const originalDetails: Row[] = rowSelected?.upgradeDetails || [];
  const details: Row[] = [];

  const detailCode = (d: Row) => d.maSanPhamMoi || d.maSanPham || '';

  summaries.forEach(summary => {
    // Summary mới (id = 0) chưa sở hữu detail nào đã lưu; detail KT thêm được gửi ở bước orphan bên dưới.
    // Không lọc theo summaryId === 0 vì sẽ gom nhầm MỌI detail mồ côi vào từng summary mới (trùng lặp).
    if (!summary.id) return;

    const original = rowSelected?.upgradeSummaries?.find((s: Row) => s.id === summary.id);
    const isChangedKit = original && original.maSanPhamMoi !== summary.maSanPhamMoi;
    const linked = originalDetails.filter(d => d.summaryId === summary.id || d.idSumaries === summary.id);

    if (!isChangedKit) {
      // Không đổi -> giữ linh kiện cũ từ DB
      linked.forEach(d => details.push(formatItem(d, false, ctx)));
      return;
    }

    const set = findSet(productSets, summary.maSanPhamMoi);

    // Đổi sang SP lẻ (vd KD chấp nhận "KT đổi"): giữ detail KT đã lắp đúng mã mới -> không mất serial/SĐT
    if (!set?.details) {
      linked.filter(d => detailCode(d) === summary.maSanPhamMoi).forEach(d => details.push(formatItem(d, false, ctx)));
      return;
    }

    // Đổi bộ -> rã lại theo bộ mới; thành phần nào KT đã lắp đúng mã thì dùng lại detail cũ
    // (giữ id + serial/SĐT), còn lại tạo mới
    const reusable = [...linked];
    const takeReusable = (code: string) => {
      const idx = reusable.findIndex(d => detailCode(d) === code);
      return idx === -1 ? undefined : reusable.splice(idx, 1)[0];
    };

    set.details.forEach(({ productInfo, ...rest }: Row) => {
      const isMain = productInfo?.input === 1 || rest?.input === 1;
      const raw = {
        ...rest,
        ...productInfo,
        id: 0,
        summaryId: summary.id,
        vehicleType: summary.vehicleType,
        registrationPlate: summary.registrationPlate?.trim(),
        companyId: summary.companyId,
        doiXe: summary.doiXe,
        hanDangKiem: summary.hanDangKiem,
        maSanPhamCu: isMain ? summary.maSanPhamCu : ''
      };

      const formatted = formatItem(raw, false, ctx);
      const amount = raw.amount || 1;
      const ids = raw.ids || [];

      const withReuse = (base: Row, fallbackId: number) => {
        const old = takeReusable(base.maSanPhamMoi);
        if (!old) return { ...base, id: fallbackId };
        return {
          ...base,
          id: old.id,
          serial: old.serial || '',
          phone: old.phone || '',
          serialCu: old.serialCu || '',
          phoneCu: old.phoneCu || '',
          note: old.note || base.note,
          staffExecute: old.staffExecute
        };
      };

      if (amount > 1) for (let k = 0; k < amount; k++) details.push(withReuse(formatted, ids[k] || 0));
      else details.push(withReuse(formatted, 0));
    });
  });

  // SP kỹ thuật thêm mà KD giữ lại (dòng còn orphanDetailId): gửi kèm detail gốc của KT để không bị mất
  // serial/SĐT. summaryId = 0 vì summary mới chưa có id -> backend gắn theo xe + công ty + mã SP.
  // Dòng KD từ chối đã bị xoá khỏi form nên detail tương ứng không được gửi.
  const sentDetailIds = new Set(details.map(d => d.id).filter(Boolean));
  formItems.forEach(item => {
    const orphan = item.orphanDetailId && originalDetails.find(d => d.id === item.orphanDetailId);
    if (!orphan || sentDetailIds.has(orphan.id)) return;
    sentDetailIds.add(orphan.id);
    details.push({ ...formatItem(orphan, false, ctx), summaryId: 0 });
  });

  return { summaries, details, isNotEnoughData: false };
}

export function buildUpgradePayload(args: {
  groups?: Row[]; // event.UpgradeItems
  isTechnician: boolean;
  rowSelected: Row;
  productSets?: Row[];
  useVAT: boolean;
  staffCreate: string;
}) {
  const { groups = [], isTechnician, rowSelected, productSets, useVAT, staffCreate } = args;
  const ctx: PayloadCtx = { rowSelected, useVAT };

  const formItems = groups.flatMap(group => [
    ...(group.products || []).flatMap((i: Row) => expandItem(i, group)),
    ...(group.details || []).flatMap((i: Row) => expandItem(i, group))
  ]);

  const { summaries, details, isNotEnoughData } = isTechnician
    ? buildTechnicianPayload(formItems, ctx)
    : buildSalesPayload(formItems, productSets, ctx);

  const isEmpty = summaries.length === 0 && details.length === 0;

  const finalDetails = (isTechnician ? details : syncCostSummaryToDetails(summaries, details, productSets, useVAT)).map(
    d => ({ ...d, staffCreate })
  );

  return { summaries, details: finalDetails, isEmpty, isNotEnoughData };
}
